import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

// Rendered reports live on the public site (racemarks.app/analysis/...), a
// different origin to the committee portal. Configurable per-env, defaulting to
// production — report URLs are content-addressed by token, so the same path
// resolves on whichever site hosts the render.
export const ANALYSIS_BASE_URL =
  import.meta.env.VITE_ANALYSIS_BASE_URL || 'https://racemarks.app';

/** Public render path for a report row. Reads the stored reportPath, falling
 *  back to building it from the row's parts (slug/date/vessel/token). */
export function reportPath(row) {
  if (row.reportPath) return row.reportPath;
  const { slug, date, vessel, token } = row;
  if (!slug || !date || !vessel || !token) return null;
  return `/analysis/${slug}/${date}/${vessel}/${token}/`;
}

/** Absolute URL to the rendered report, or null if the row lacks a path. */
export function reportUrl(row, base = ANALYSIS_BASE_URL) {
  const path = reportPath(row);
  return path ? `${base}${path}` : null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** ISO date (YYYY-MM-DD) → "30 May 2026"; passes through anything unexpected. */
export function formatReportDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return iso || '';
  const [, y, mo, d] = m;
  return `${parseInt(d, 10)} ${MONTHS[parseInt(mo, 10) - 1]} ${y}`;
}

/** Sort newest-first by date, then by vessel for a stable order within a day.
 *  Returns a new array (does not mutate the input). */
export function sortReportsByDateDesc(rows) {
  return [...rows].sort((a, b) => {
    const byDate = (b.date || '').localeCompare(a.date || '');
    if (byDate !== 0) return byDate;
    return (a.vessel || '').localeCompare(b.vessel || '');
  });
}

/** Load all race-report index rows for a club, newest-first.
 *  Equality-only query (clubId) uses the automatic single-field index; sorting
 *  is done client-side so no composite index is required. */
export async function loadClubReports(clubId) {
  const q = query(collection(db, 'analysis_index'), where('clubId', '==', clubId));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sortReportsByDateDesc(rows);
}
